import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarX, BadgeCheck, Clock } from "lucide-react";

/* ============================================================
   Shared UI kit for the UPPIT 2.0 design system.
   Ivory / obsidian / electric-blue. Cabinet Grotesk + Satoshi.
   ============================================================ */

const cx = (...c) => c.filter(Boolean).join(" ");

const VARIANTS = {
  primary:
    "bg-electric text-white hover:bg-electric-600 hover:shadow-glow-blue",
  light:
    "bg-coal text-ivory hover:bg-obsidian-700 hover:shadow-card-lift",
  outline:
    "bg-transparent border border-coal/20 text-coal hover:border-electric hover:text-electric",
  outlineLight:
    "bg-transparent border border-white/25 text-ivory hover:border-electric hover:text-white",
  ghost:
    "bg-white text-coal hover:bg-electric hover:text-white shadow-card-light",
};

/**
 * Button — pill CTA. Renders <Link>, <a>, or <button> based on props.
 */
export const Button = ({
  variant = "primary",
  to,
  href,
  children,
  className = "",
  withArrow = false,
  testid,
  ...rest
}) => {
  const base =
    "group relative inline-flex items-center justify-center gap-2.5 whitespace-nowrap [&_svg]:shrink-0 rounded-full px-7 py-3.5 font-semibold text-sm tracking-tight transition-all duration-300 will-change-transform";
  const cls = cx(base, VARIANTS[variant], className);
  const inner = (
    <>
      <span>{children}</span>
      {withArrow && (
        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
      )}
    </>
  );
  if (to)
    return (
      <Link to={to} data-testid={testid} className={cls} {...rest}>
        {inner}
      </Link>
    );
  if (href)
    return (
      <a data-testid={testid} href={href} className={cls} {...rest}>
        {inner}
      </a>
    );
  return (
    <button data-testid={testid} className={cls} {...rest}>
      {inner}
    </button>
  );
};

/**
 * Eyebrow — overline kicker label with a leading line + dot.
 */
export const Eyebrow = ({ children, dark = false, className = "" }) => (
  <div className={cx("flex items-center gap-3", className)}>
    <span className={cx("w-8 h-px", dark ? "bg-electric-400" : "bg-electric")} />
    <span
      className={cx(
        "font-mono text-[11px] uppercase tracking-[0.28em]",
        dark ? "text-white/60" : "text-coal/55"
      )}
    >
      {children}
    </span>
  </div>
);

/**
 * Chip — small pill tag.
 */
export const Chip = ({ children, dark = false, className = "" }) => (
  <span
    className={cx(
      "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium",
      dark
        ? "bg-white/10 border border-white/10 text-white/80"
        : "bg-electric/10 border border-electric/15 text-electric-700",
      className
    )}
  >
    {children}
  </span>
);

export { cx };

export const smoothScrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const y = el.getBoundingClientRect().top + window.scrollY - 112;
  if (window.__lenis) window.__lenis.scrollTo(y, { duration: 1 });
  else window.scrollTo({ top: y, behavior: "smooth" });
};

export const ASSURANCE_ITEMS = [
  { icon: CalendarX, text: "Maandelijks opzegbaar" },
  { icon: BadgeCheck, text: "Geen setup-kosten" },
  { icon: Clock, text: "Reactie binnen 24u" },
];

/**
 * Assurances — risk-reversal badges row.
 */
export const Assurances = ({ dark = false, items = ASSURANCE_ITEMS, className = "", testid = "assurances" }) => (
  <div data-testid={testid} className={cx("flex flex-wrap items-center gap-x-6 gap-y-2.5", className)}>
    {items.map(({ icon: Icon, text }) => (
      <span
        key={text}
        className={cx(
          "inline-flex items-center gap-1.5 text-sm",
          dark ? "text-white/60" : "text-coal/55"
        )}
      >
        <Icon className={cx("w-4 h-4 shrink-0", dark ? "text-electric-300" : "text-electric")} />
        {text}
      </span>
    ))}
  </div>
);
